import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { setTelemetryUser, clearTelemetryUser } from '../config/appInsights';

export interface AuthUser {
  userId: string;
  name: string;
  email: string;
  customerId: string;
  customerName: string;
  roles?: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  loginWithDemoAccount: (email: string) => boolean;
}

// Demo accounts for external access (URL-based auto-login)
const DEMO_ACCOUNTS: Record<string, Omit<AuthUser, 'roles'>> = {
  'vasakronan@demo.com': {
    userId: 'vasakronan-demo-001',
    name: 'Vasakronan Demo User',
    email: 'vasakronan@demo.com',
    customerId: 'vasakronan-demo-2025',
    customerName: 'Vasakronan',
  },
  'demo@example.com': {
    userId: 'demo-user-123',
    name: 'Demo User',
    email: 'demo@example.com',
    customerId: 'customer_demo123',
    customerName: 'Demo Company',
  },
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Attribute all usage telemetry to the signed-in user + customer. Covers
  // every login path (real SWA auth, local demo, ?demo= deep-link) since they
  // all funnel through `user` state. Clears identity on logout.
  useEffect(() => {
    if (user) {
      setTelemetryUser(user.email, user.customerId);
    } else {
      clearTelemetryUser();
    }
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      
      // Check if running locally (development mode)
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                window.location.hostname === '127.0.0.1';
      
      if (isLocalDevelopment) {
        // Check if user has explicitly logged out
        const hasLoggedOut = localStorage.getItem('demo_logged_out') === 'true';
        if (hasLoggedOut) {
          console.log('Demo user has been logged out');
          setUser(null);
          return;
        }
        
        // For local development, use a demo user
        console.log('Local development detected - using demo user');
        const demoUser: AuthUser = {
          userId: 'demo-user-123',
          name: 'Demo User',
          email: 'demo@example.com',
          customerId: 'customer_demo123',
          customerName: 'Demo Company',
          roles: ['authenticated']
        };
        setUser(demoUser);
        return;
      }
      
      // Production: Use Azure Static Web Apps authentication
      const response = await fetch('/.auth/me');
      
      if (response.ok) {
        const authData = await response.json();
        const clientPrincipal = authData.clientPrincipal;
        
        if (clientPrincipal) {
          // Extract user information from Static Web Apps auth
          const authUser: AuthUser = {
            userId: clientPrincipal.userId,
            name: clientPrincipal.userDetails,
            email: clientPrincipal.claims?.find((c: any) => c.typ === 'email')?.val || '',
            customerId: generateCustomerId(clientPrincipal.userId),
            customerName: extractCustomerName(clientPrincipal.userDetails),
            roles: clientPrincipal.userRoles || []
          };
          
          setUser(authUser);
          
          // Ensure customer is registered in backend
          await ensureCustomerRegistration(authUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // In local development, don't fail completely - provide demo user
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                window.location.hostname === '127.0.0.1';
      if (isLocalDevelopment) {
        // Check if user has explicitly logged out
        const hasLoggedOut = localStorage.getItem('demo_logged_out') === 'true';
        if (hasLoggedOut) {
          console.log('Demo user has been logged out (error fallback)');
          setUser(null);
        } else {
          console.log('Auth failed in local dev - falling back to demo user');
          const demoUser: AuthUser = {
            userId: 'demo-user-123',
            name: 'Demo User',
            email: 'demo@example.com',
            customerId: 'customer_demo123',
            customerName: 'Demo Company',
            roles: ['authenticated']
          };
          setUser(demoUser);
        }
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateCustomerId = (userId: string): string => {
    // Generate a consistent customer ID from user ID
    // In production, this would be managed through your External ID tenant configuration
    return `customer_${userId.slice(0, 8)}`;
  };

  const extractCustomerName = (userDetails: string): string => {
    // Extract customer/company name from user details
    // This could be enhanced to use custom claims from External ID
    return userDetails || 'Customer Organization';
  };

  const ensureCustomerRegistration = async (authUser: AuthUser) => {
    try {
      // Call backend API to ensure customer is registered
      const response = await fetch('/api/customers/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: authUser.customerId,
          customer_name: authUser.customerName,
          customer_email: authUser.email,
          user_id: authUser.userId,
          user_name: authUser.name
        })
      });

      if (!response.ok) {
        console.warn('Customer registration failed:', await response.text());
      }
    } catch (error) {
      console.error('Customer registration error:', error);
    }
  };

  const login = () => {
    const isLocalDevelopment = window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1';
    
    if (isLocalDevelopment) {
      // Clear logout flag and refresh to trigger auth check with demo user
      localStorage.removeItem('demo_logged_out');
      console.log('Local development login - refreshing page');
      window.location.reload();
    } else {
      // Production: redirect to Azure AD login
      window.location.href = '/.auth/login/aad';
    }
  };

  const logout = () => {
    const isLocalDevelopment = window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1';
    
    if (isLocalDevelopment) {
      // In local development, set logout flag and clear user
      console.log('Local development logout - setting logout flag');
      localStorage.setItem('demo_logged_out', 'true');
      localStorage.removeItem('auth_user');
      setUser(null);
      // No need to reload - just clear the user state
    } else {
      // Production: redirect to Azure logout
      window.location.href = '/.auth/logout';
    }
  };

  const loginWithDemoAccount = (email: string): boolean => {
    const account = DEMO_ACCOUNTS[email];
    
    if (account) {
      const demoUser: AuthUser = {
        ...account,
        roles: ['authenticated']
      };
      
      console.log('Demo account login:', email);
      setUser(demoUser);
      localStorage.setItem('auth_user', JSON.stringify(demoUser));
      localStorage.removeItem('demo_logged_out');
      
      return true;
    }
    
    console.error('Demo account not found:', email);
    return false;
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      login,
      logout,
      loginWithDemoAccount
    }}>
      {children}
    </AuthContext.Provider>
  );
};
